import React, { useState } from 'react';
import api from '../../../utils/api';

const CATEGORIES = ['Meat', 'Mutton', 'Offal', 'Processed', 'Other'];
const UNITS      = ['kg', 'g', 'piece', 'dozen'];

const EMPTY_FORM = {
  name: '', category: 'Meat', description: '',
  price_inr: '', weight_kg: '', unit: 'kg', stock_qty: '',
};


export default function AddProductPanel({ onProductAdded }) {
  const [form, setForm]           = useState(EMPTY_FORM);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState('');
  const [success, setSuccess]     = useState(false);

  /* ── helpers ── */
  const handleChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleImage = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setImageFile(null);
    setImagePreview(null);
    setError('');
  };

  /* ── submit ── */
  const handleSubmit = async () => {
    if (!form.name.trim())   { setError('Product name is required.');  return; }
    if (!form.price_inr)     { setError('Price is required.');          return; }
    if (!form.stock_qty || Number(form.stock_qty) < 0) { setError('Stock quantity is required and must be at least 0.');  return; }

    setSaving(true);
    setError('');
    setSuccess(false);

    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (imageFile) fd.append('product_image', imageFile);

      await api.post('/api/butcher/shop', fd);

      setSuccess(true);
      resetForm();
      if (onProductAdded) onProductAdded(); // tell ShopPanel to re-fetch
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add product. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  /* ── shared input class ── */
  const inputCls =
    'w-full px-3 py-2 bg-surface-container-low border border-outline-variant/30 ' +
    'rounded-lg text-sm focus:ring-1 focus:ring-primary outline-none transition-all';

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-outline-variant/10">

        {/* ── Header ── */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <span className="material-symbols-outlined text-primary text-[22px]">add_box</span>
          </div>
          <div>
            <h3 className="font-bold text-lg leading-tight">Add New Product</h3>
            <p className="text-[11px] text-secondary">List a new item in your shop</p>
          </div>
        </div>

        {/* ── Alerts ── */}
        {error && (
          <div className="mb-4 flex items-start gap-2 px-4 py-3 bg-red-50 border border-red-100 rounded-xl">
            <span className="material-symbols-outlined text-red-500 text-[18px] mt-0.5">error</span>
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        )}
        {success && (
          <div className="mb-4 flex items-center gap-2 px-4 py-3 bg-emerald-50 border border-emerald-100 rounded-xl">
            <span className="material-symbols-outlined text-emerald-500 text-[18px]">check_circle</span>
            <p className="text-emerald-700 text-sm font-medium">Product added successfully!</p>
          </div>
        )}

        {/* ── Image preview strip ── */}
        <div className="mb-5">
          <label className="text-[10px] font-bold uppercase tracking-widest text-secondary block mb-2">
            Product Image
          </label>
          <div className="flex items-center gap-4">
            <div className={`w-24 h-24 rounded-xl border-2 border-dashed flex items-center justify-center overflow-hidden
                            ${imagePreview ? 'border-primary/30' : 'border-outline-variant/40 bg-surface-container-low'}`}>
              {imagePreview
                ? <img src={imagePreview} alt="preview" className="w-full h-full object-cover" />
                : <span className="material-symbols-outlined text-3xl text-outline-variant">image</span>
              }
            </div>
            <div className="flex-1">
              <input
                type="file"
                accept="image/*"
                onChange={handleImage}
                className="text-sm text-secondary
                           file:mr-3 file:px-4 file:py-1.5 file:rounded-full file:border-0
                           file:bg-primary/10 file:text-primary file:font-bold file:text-xs
                           cursor-pointer"
              />
              <p className="mt-1 text-[10px] text-secondary">JPG, PNG, WEBP — max 5 MB</p>
              {imageFile && (
                <button
                  onClick={() => { setImageFile(null); setImagePreview(null); }}
                  className="mt-1 text-[11px] text-red-500 font-medium hover:underline"
                >
                  Remove
                </button>
              )}
            </div>
          </div>
        </div>

        {/* ── Form grid ── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

          {/* Name */}
          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-secondary block mb-1">
              Product Name <span className="text-red-500">*</span>
            </label>
            <input
              name="name"
              value={form.name}
              onChange={handleChange}
              className={inputCls}
              placeholder="e.g. Fresh Mutton Shoulder"
            />
          </div>

          {/* Category */}
          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-secondary block mb-1">
              Category
            </label>
            <select name="category" value={form.category} onChange={handleChange} className={inputCls}>
              {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>

          {/* Price */}
          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-secondary block mb-1">
              Price (₹) <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-secondary text-sm font-bold">₹</span>
              <input
                name="price_inr"
                type="number"
                min="0"
                value={form.price_inr}
                onChange={handleChange}
                className={`${inputCls} pl-7`}
                placeholder="500"
              />
            </div>
            {Number(form.price_inr) > 0 && Number(form.stock_qty) >= 1 && (
              <div className="mt-1.5 flex items-center justify-between px-2.5 py-1.5 bg-emerald-50 border border-emerald-200 rounded-lg">
                <span className="text-[11px] text-emerald-700 font-semibold">
                  ₹{Number(form.price_inr).toLocaleString('en-IN')} × {form.stock_qty} units
                </span>
                <span className="text-[13px] text-emerald-800 font-black">
                  = ₹{(Number(form.price_inr) * Number(form.stock_qty)).toLocaleString('en-IN')} total stock value
                </span>
              </div>
            )}
          </div>

          {/* Weight */}
          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-secondary block mb-1">
              Weight (kg)
            </label>
            <input
              name="weight_kg"
              type="number"
              min="0"
              step="0.1"
              value={form.weight_kg}
              onChange={handleChange}
              className={inputCls}
              placeholder="1"
            />
          </div>

          {/* Unit */}
          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-secondary block mb-1">
              Unit
            </label>
            <select name="unit" value={form.unit} onChange={handleChange} className={inputCls}>
              {UNITS.map((u) => <option key={u}>{u}</option>)}
            </select>
          </div>

          {/* Stock */}
          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-secondary block mb-1">
              Stock Qty (units available) <span className="text-red-500">*</span>
            </label>
            <input
              name="stock_qty"
              type="number"
              min="0"
              required
              value={form.stock_qty}
              onChange={handleChange}
              className={inputCls}
              placeholder="e.g. 5"
            />
          </div>

          {/* Description */}
          <div className="sm:col-span-2">
            <label className="text-[10px] font-bold uppercase tracking-widest text-secondary block mb-1">
              Description
            </label>
            <textarea
              name="description"
              value={form.description}
              onChange={handleChange}
              rows={3}
              className={inputCls}
              placeholder="Describe the product — cut, quality, sourcing, etc."
            />
          </div>
        </div>

        {/* ── Actions ── */}
        <div className="flex gap-3 mt-6">
          <button
            onClick={resetForm}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-outline-variant/30
                       text-sm font-semibold text-on-surface-variant
                       hover:bg-surface-container transition-all"
          >
            <span className="material-symbols-outlined text-[18px]">refresh</span>
            Reset
          </button>

          <button
            onClick={handleSubmit}
            disabled={saving}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl
                       bg-primary text-white font-bold text-sm
                       hover:opacity-90 transition-all disabled:opacity-50 shadow-sm"
          >
            {saving ? (
              <>
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Adding Product...
              </>
            ) : (
              <>
                <span className="material-symbols-outlined text-[18px]">add_circle</span>
                Add Product
              </>
            )}
          </button>
        </div>

      </div>
    </div>
  );
}
